import { proxy } from "valtio";
import {
  trackOnepageGuideComplete,
  trackOnepageGuideSkip,
  trackOnepageGuideStart,
} from "./onepageGuideAnalytics";
import { markOnepageGuideSeen } from "./onepageGuideStorage";
import type {
  OnepageGuideEndedOn,
  OnepageGuideLandingTab,
  OnepageGuideState,
} from "./onepageGuideTypes";

const initialState = (): OnepageGuideState => ({
  active: false,
  hasGoal: false,
  degraded: false,
  landingTab: "plan",
  shownAt: 0,
  reducedMotion: false,
});

/** 一屏引导会话（fe-spec §2.2）。`active` 供其他自动弹出面让行（如 Report 开场页）。 */
export const onepageGuideStore = proxy<OnepageGuideState>(initialState());

export function readPrefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) {
    return false;
  }
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export function isOnepageGuideActive(): boolean {
  return onepageGuideStore.active;
}

/**
 * 弹出：写 `seen`（兑现，AC-17）+ 发 `handoff_start`。同会话重复调用 no-op。
 */
export function showOnepageGuide(params: {
  ownerId: string;
  hasGoal: boolean;
  degraded: boolean;
  landingTab: OnepageGuideLandingTab;
}): boolean {
  if (onepageGuideStore.active) {
    return false;
  }
  const reducedMotion = readPrefersReducedMotion();
  markOnepageGuideSeen(params.ownerId);
  Object.assign(onepageGuideStore, {
    active: true,
    hasGoal: params.hasGoal,
    degraded: params.degraded,
    landingTab: params.landingTab,
    shownAt: Date.now(),
    reducedMotion,
  } satisfies OnepageGuideState);
  trackOnepageGuideStart({
    has_goal: params.hasGoal,
    degraded: params.degraded,
    landing_tab: params.landingTab,
    reduced_motion: reducedMotion,
  });
  return true;
}

/** 下一步 CTA：发 `handoff_complete` 并关闭；导航由调用方在关闭后执行。 */
export function completeOnepageGuide(): OnepageGuideEndedOn | null {
  if (!onepageGuideStore.active) {
    return null;
  }
  const endedOn: OnepageGuideEndedOn = onepageGuideStore.hasGoal
    ? "plan"
    : "brief";
  trackOnepageGuideComplete(endedOn);
  Object.assign(onepageGuideStore, initialState());
  return endedOn;
}

/** Skip：发 `handoff_skip{dwell_ms}` 并关闭，不导航（AC-16）。 */
export function skipOnepageGuide(): void {
  if (!onepageGuideStore.active) {
    return;
  }
  trackOnepageGuideSkip(Date.now() - onepageGuideStore.shownAt);
  Object.assign(onepageGuideStore, initialState());
}

/** 宿主卸载 / 路由失配时静默收起（无埋点）；`seen` 已写，不会再弹。 */
export function teardownOnepageGuide(): void {
  if (!onepageGuideStore.active) {
    return;
  }
  Object.assign(onepageGuideStore, initialState());
}

/** 测试复位 */
export function resetOnepageGuideStore(): void {
  Object.assign(onepageGuideStore, initialState());
}
